import { datadogRum } from "@datadog/browser-rum";
import { Routes } from "@datadog/browser-rum-react/react-router-v7";
import { analyticsEvents, analyticsScreenForPath } from "@healthos/analytics";
import {
  type HostAuthErrorReason,
  isHostAuthActive,
  isImpersonationActive,
  useAuth,
} from "@healthos/auth/react";
import {
  getCoachingProgrammes,
  getCompanyWalkingChallenge,
  libraryRecommendationsQuery,
  queryKeys,
} from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { careWords } from "@healthos/shared";
import { Button, ThriveLeaf } from "@healthos/ui";
import { AppShell, DEFAULT_NAV_ITEMS, type DefaultNavItem } from "@healthos/ui/shell";
import { ChatsCircle, FirstAidKit, Headset, ShieldCheck } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { client, jsonOrThrow } from "./api/healthos";
import {
  COMMUNITY_CAPABILITIES_QUERY_KEY,
  useCommunityCapabilities,
} from "./api/use-community-capabilities";
import { ChallengeEntry, challengeRoutes } from "./challenge-routes";
import {
  AmbiencePlayerProvider,
  useAmbience,
} from "./components/ambience/ambience-player-provider";
import { AwayRefreshModal } from "./components/away-refresh-modal";
import { BootProvider, useBoot } from "./components/boot/boot-context";
import { BroadcastHost } from "./components/broadcast/broadcast-host";
import { CrisisNotice } from "./components/crisis-notice";
import { DemoControlPanel } from "./components/dev/demo-control-panel";
import { PreLoginDemoButton } from "./components/dev/pre-login-demo-button";
import { ImpersonationBanner } from "./components/impersonation-banner";
import { InAppAttentionToast } from "./components/in-app-attention-toast";
import { LegacyCommunityRedirect } from "./components/legacy-community-redirect";
import { ManualLogProvider } from "./components/manual-log-context";
import { ManualSignalLogModal } from "./components/manual-signal-log-modal";
import type { ManualLogPreselect } from "./components/manual-signal-log-model";
import { MediaPlayerProvider } from "./components/media-player/media-player-provider";
import { MediaPlayerSidebarDock } from "./components/media-player/media-player-sidebar-dock";
import { OpsShell, RequireOpsCap } from "./components/ops/ops-shell";
import { opsBrandFor } from "./components/ops/ops-side-nav";
import { ProfileNavTrigger } from "./components/profile-nav-trigger";
import { RootErrorBoundary } from "./components/root-error-boundary";
import { RouteFallback } from "./components/route-fallback";
import { RouteMilestoneAnnouncer } from "./components/route-milestone-announcer";
import { ScrollToTop } from "./components/scroll-to-top";
import { ContactCentreHome } from "./contact-centre/contact-centre-home";
import { ContactCentreShell, useContactCentreMe } from "./contact-centre/contact-centre-shell";
import { readEnv } from "./env";
import { useAttentionEvents } from "./hooks/use-attention-events";
import { syncWebPushSubscription } from "./hooks/use-attention-notification";
import { useBranchDeepLink } from "./hooks/use-branch-deep-link";
import { useDayZeroTour } from "./hooks/use-day-zero-tour";
import { useMemberName } from "./hooks/use-member-name";
import { useUnreadNotificationCount } from "./hooks/use-notifications";
import { useReturnTracking } from "./hooks/use-return-tracking";
import { useServiceWorkerNotificationSync } from "./hooks/use-service-worker-messages";
import { useTeamsTabLink } from "./hooks/use-teams-tab-link";
import { useUnreadKudos } from "./hooks/use-unread-kudos";
import { installAbortSafeViewTransitions } from "./lib/abort-safe-view-transitions";
import { analytics } from "./lib/analytics";
import { clearDatadogUser, setDatadogUser, setDatadogUserCompany } from "./lib/datadog";
import { hasContactCentreRole } from "./lib/healthos-access";
import {
  readLastUsedHomeSurface,
  resolveHomeSurface,
  writeLastUsedHomeSurface,
} from "./lib/home-surface";
import { localDevSignedIn } from "./lib/local-dev-auth";
import { hydrateNotificationSoundPref } from "./lib/notification-sound";
import { rememberOnboardingReturnTo } from "./lib/onboarding-return-to";
import { usePageAccent } from "./lib/page-accent";
import { hydrateSoundPref, initSound, playSound } from "./lib/sound";
import { initTheme } from "./lib/theme";
import { useDarkModeGate } from "./lib/use-dark-mode-access";
import { ScreenAccessDenied } from "./pages/onboarding/screen-access-denied";
import { useOperationsMe } from "./pages/operations/dashboard/_hooks";
import { HealthOsCommandBar } from "./prybar/command-bar";
import { HealthOsCommandTrigger } from "./prybar/command-trigger";
import { PrybarScreenProvider } from "./prybar/screen-context";
import { clearPrybarStorage, purgeOtherUsersPrybarSessions } from "./prybar/session-cleanup";
import { openFeedbackSurvey } from "./prybar/spark";

/**
 * Route-level code splitting.
 *
 * Every page is `React.lazy()`-loaded so first-paint only pays for the
 * ambient surface (the default landing page). Snapshot, settings,
 * connections, and OIDC callback chunks stream in on demand.
 *
 * Per the 2026 production-readiness checklist: Suspense fallbacks are
 * dimension-matched splash surfaces, not generic spinners, so swapping the
 * skeleton for real content does not bump CLS.
 */
const importAmbientPage = () =>
  import("./pages/ambient-page").then((m) => ({ default: m.AmbientPage }));
const AmbientPage = lazy(importAmbientPage);
// Warm the landing chunk during boot so the splash hands straight to the Today
// skeleton instead of flashing the generic route fallback first, and so the page
// mounts before its data resolves (skeleton then view-transition crossfade) rather
// than mounting already-ready and playing a redundant staggered entrance.
if (typeof window !== "undefined") void importAmbientPage();
const CallbackPage = lazy(() =>
  import("./pages/callback-page").then((m) => ({ default: m.CallbackPage })),
);
const ConnectorsPage = lazy(() =>
  import("./pages/connectors-page").then((m) => ({ default: m.ConnectorsPage })),
);
const DesktopAppPage = lazy(() =>
  import("./pages/desktop-app-page").then((m) => ({ default: m.DesktopAppPage })),
);
const SignupLinkPage = lazy(() =>
  import("./pages/signup-link-page").then((m) => ({ default: m.SignupLinkPage })),
);
const InstallLandingPage = lazy(() =>
  import("./pages/install-landing-page").then((m) => ({ default: m.InstallLandingPage })),
);
const CoachingPage = lazy(() =>
  import("./pages/coaching/coaching-page").then((m) => ({ default: m.CoachingPage })),
);
const CoachingJoinPage = lazy(() =>
  import("./pages/coaching/join-page").then((m) => ({ default: m.CoachingJoinPage })),
);
const LinkChatPage = lazy(() =>
  import("./pages/link-chat-page").then((m) => ({ default: m.LinkChatPage })),
);
const MyLibraryPage = lazy(() =>
  import("./pages/my-library-page").then((m) => ({ default: m.MyLibraryPage })),
);
const CommunityPage = lazy(() =>
  import("./pages/community-page").then((m) => ({ default: m.CommunityPage })),
);
const CommunityAdminPage = lazy(() =>
  import("./pages/community-admin-page").then((m) => ({ default: m.CommunityAdminPage })),
);
const ChallengesAdminPage = lazy(() =>
  import("./pages/challenges-admin-page").then((m) => ({ default: m.ChallengesAdminPage })),
);
const NotificationsPage = lazy(() =>
  import("./pages/notifications-page").then((m) => ({ default: m.NotificationsPage })),
);
const FingerprintPage = lazy(() =>
  import("./pages/fingerprint-page").then((m) => ({ default: m.FingerprintPage })),
);

// Old routes live on in muscle memory and stored links; carry the query string
// across so a direct card link keeps its intended destination.
function LegacyRouteRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}
/*
 * /explore is the hub, but content links minted before it existed (pushes,
 * emails, the Heartbeat reset handoff, Prybar cards) all say /explore?<query>.
 * Any query means "open this in the collection", so it goes there intact.
 */
function ExploreEntry() {
  const { search } = useLocation();
  if (search) return <Navigate to={`/explore/collection${search}`} replace />;
  return <ExploreHubPage />;
}
const OperationsLayout = lazy(() =>
  import("./pages/operations/dashboard/_layout").then((m) => ({ default: m.OperationsLayout })),
);
const OperationsOverview = lazy(() =>
  import("./pages/operations/dashboard/overview").then((m) => ({ default: m.OperationsOverview })),
);
const OperationsCohort = lazy(() =>
  import("./pages/operations/dashboard/cohort").then((m) => ({ default: m.OperationsCohort })),
);
const OperationsCalendar = lazy(() =>
  import("./pages/operations/dashboard/calendar").then((m) => ({
    default: m.OperationsCalendar,
  })),
);
const OperationsMethodology = lazy(() =>
  import("./pages/operations/dashboard/methodology").then((m) => ({
    default: m.OperationsMethodology,
  })),
);
const OpsPeoplePage = lazy(() =>
  import("./pages/operations/people-page").then((m) => ({ default: m.OpsPeoplePage })),
);
const OpsSignalsPage = lazy(() =>
  import("./pages/operations/signals-page").then((m) => ({ default: m.OpsSignalsPage })),
);
const OpsCortexLoopHealthPage = lazy(() =>
  import("./pages/operations/cortex-loop-health-page").then((m) => ({
    default: m.OpsCortexLoopHealthPage,
  })),
);
const OpsCortexPerformancePage = lazy(() =>
  import("./pages/operations/cortex-performance-page").then((m) => ({
    default: m.OpsCortexPerformancePage,
  })),
);
const OpsCortexScorerQueuePage = lazy(() =>
  import("./pages/operations/cortex-scorer-queue-page").then((m) => ({
    default: m.OpsCortexScorerQueuePage,
  })),
);
const OpsCortexProtocolsPage = lazy(() =>
  import("./pages/operations/cortex-protocols-page").then((m) => ({
    default: m.OpsCortexProtocolsPage,
  })),
);
const OpsCortexTracePage = lazy(() =>
  import("./pages/operations/cortex-trace-page").then((m) => ({
    default: m.OpsCortexTracePage,
  })),
);
const OpsCortexMemberPage = lazy(() =>
  import("./pages/operations/cortex-member-page").then((m) => ({
    default: m.OpsCortexMemberPage,
  })),
);
const OpsEscalationsPage = lazy(() =>
  import("./pages/operations/escalations-page").then((m) => ({ default: m.OpsEscalationsPage })),
);
const CoachLayout = lazy(() =>
  import("./pages/operations/coaching/coach-layout").then((m) => ({ default: m.CoachLayout })),
);
const CoacheesPage = lazy(() =>
  import("./pages/operations/coaching/coachees-page").then((m) => ({ default: m.CoacheesPage })),
);
const CoacheePage = lazy(() =>
  import("./pages/operations/coaching/coachee-page").then((m) => ({ default: m.CoacheePage })),
);
const SchedulePage = lazy(() =>
  import("./pages/operations/coaching/schedule-page").then((m) => ({ default: m.SchedulePage })),
);
const AvailabilityPage = lazy(() =>
  import("./pages/operations/coaching/availability-page").then((m) => ({
    default: m.AvailabilityPage,
  })),
);
const CoachingOpsPage = lazy(() =>
  import("./pages/operations/coaching/coaching-ops-page").then((m) => ({
    default: m.CoachingOpsPage,
  })),
);
const OpsBillingPage = lazy(() =>
  import("./pages/operations/billing-page").then((m) => ({ default: m.OpsBillingPage })),
);
const OpsOrgIntegrationsPage = lazy(() =>
  import("./pages/operations/org-integrations-page").then((m) => ({
    default: m.OpsOrgIntegrationsPage,
  })),
);
const OpsIntegrationsPage = lazy(() =>
  import("./pages/operations/integrations-page").then((m) => ({
    default: m.OpsIntegrationsPage,
  })),
);
const OpsSettingsPage = lazy(() =>
  import("./pages/operations/ops-settings-page").then((m) => ({ default: m.OpsSettingsPage })),
);
const OpsBroadcastsPage = lazy(() =>
  import("./pages/operations/broadcasts-page").then((m) => ({ default: m.OpsBroadcastsPage })),
);
const OpsEmailTemplatesPage = lazy(() =>
  import("./pages/operations/email-templates-page").then((m) => ({
    default: m.OpsEmailTemplatesPage,
  })),
);
const OpsIntegrationsAdminPage = lazy(() =>
  import("./pages/operations/integrations-admin-page").then((m) => ({
    default: m.OpsIntegrationsAdminPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("./pages/onboarding").then((m) => ({ default: m.OnboardingPage })),
);

if (typeof document !== "undefined") installAbortSafeViewTransitions();

const ONBOARDING_COMPLETE_KEY = "thrive_onboarding_complete";

/**
 * Routes that render with no reference to auth state at all.
 *
 * Not the same idea as "declared past the auth gate". Several auth states
 * return their own screen above `<Routes>` (the unconfigured screen, the
 * host-auth loader, the host-auth error surface), so a route declared past
 * them is only public when auth happens to be configured and standalone. A
 * page whose whole job is to work for somebody with no account cannot depend
 * on that: `/install-landing` goes on a poster or in a launch email, and
 * `/desktop/open` is a friendly launch link, and both have to render for a
 * visitor we know nothing about. Both are returned before any of those
 * branches, and both are folded into NO_SPLASH_PATHS below, because a page
 * with nothing to wait for must not sit behind the boot splash either.
 */
const PUBLIC_PATHS = ["/install-landing", "/desktop/open"];

// Routes that must NEVER show the boot splash on first paint regardless
// of localStorage state. Ordered by frequency to short-circuit fast.
const NO_SPLASH_PATHS = [
  "/onboarding",
  "/callback",
  "/silent-callback",
  "/signed-out",
  ...PUBLIC_PATHS,
];

/** How long auth may take before the splash is worth showing to cover it. */
const SLOW_AUTH_SPLASH_DELAY_MS = 600;

interface WhoamiResponse {
  userId: string;
  /** Covered entity, for RUM attribution. Server-authoritative; see the schema. */
  companyId?: string | null;
  onboardingComplete: boolean;
  /** Day zero for the return curve. Null until the member has onboarded. */
  onboardingCompletedAt?: string | null;
  hasCompanionAccess?: boolean;
  hasContactCentreAccess?: boolean;
}

type NavProfileResponse = {
  success?: boolean;
  data?: {
    sound?: unknown;
    notificationSound?: unknown;
  } | null;
};

function localOnboardingFlag(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true";
  } catch {
    return false;
  }
}

function setLocalOnboardingFlag(value: boolean): void {
  try {
    if (value) localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
    else localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
  } catch {
    /* localStorage unavailable; the in-memory react-query cache still works */
  }
}

function hasPostOnboardingHandoff(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      window.sessionStorage.getItem("thrive_post_onboarding_boot") === "1"
    );
  } catch {
    return false;
  }
}

function hasOidcSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("oidc.user:")) return true;
    }
  } catch {
    /* storage unavailable */
  }
  return false;
}

// The first path segment names the top-level destination the app shell nav
// switches between ("explore", "settings", "" for /today). Sub-routes like
// /challenges/leaderboards share a section, so the page cue does not fire on a
// within-section tab switch. See the route-change effect in AppInner.
function topLevelSection(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

function shouldBootSplashOnFirstPaint(): boolean {
  // Embedded host-auth owns its own loading/error surface (G2). The boot splash
  // keys off stale OIDC localStorage a returning desktop user may still carry,
  // so skip it here or it covers HostAuthLoading and delays hostError.
  if (isHostAuthActive()) return false;
  if (!localOnboardingFlag()) return false;
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  if (NO_SPLASH_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
    return false;
  }
  if (!hasOidcSession() && !localDevSignedIn()) return false;
  // A member who is already set up has nothing to wait for. The returning-user
  // splash runs the fixed "quick" preset and finishes itself (see BOOT_PRESETS),
  // so it is a scripted delay in front of an app that is ready, and it replays on
  // every hard load. Keep it for the post-onboarding handover, where it covers
  // the real wait for the first plan and is driven by that fetch.
  return hasPostOnboardingHandoff();
}
const ExploreHubPage = lazy(() =>
  import("./pages/explore/explore-hub-page").then((m) => ({ default: m.ExploreHubPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/settings-page").then((m) => ({ default: m.SettingsPage })),
);
const ProfilePage = lazy(() =>
  import("./pages/profile-page").then((m) => ({ default: m.ProfilePage })),
);
const SnapshotPage = lazy(() =>
  import("./pages/snapshot-page").then((m) => ({ default: m.SnapshotPage })),
);
const LogbookPage = lazy(() =>
  import("./pages/logbook-page").then((m) => ({ default: m.LogbookPage })),
);
const AnatomyPage = lazy(() =>
  import("./pages/anatomy/anatomy-page").then((m) => ({ default: m.AnatomyPage })),
);
const DesignSystemPage = lazy(() =>
  import("./pages/design-system-page").then((m) => ({ default: m.DesignSystemPage })),
);
const SignedOutPage = lazy(() =>
  import("./pages/signed-out-page").then((m) => ({ default: m.SignedOutPage })),
);
const SilentCallbackPage = lazy(() =>
  import("./pages/silent-callback-page").then((m) => ({ default: m.SilentCallbackPage })),
);

export function App() {
  const env = readEnv();
  // The boot splash is a "you're entering your day" moment, not a generic
  // first-paint cover. Only mount it visible on cold start when:
  //   (a) the cached onboarding flag suggests a returning onboarded user
  //   (so the destination is /today), AND
  //   (b) the current URL isn't a route that has its own pre-auth /
  //   onboarding surface (signed-out, /onboarding, OIDC callbacks).
  return (
    <AmbiencePlayerProvider>
      <BootProvider initialMode="quick" initiallyVisible={shouldBootSplashOnFirstPaint()}>
        <CrisisNotice />
        <AppInner devMode={env.devMode} />
      </BootProvider>
    </AmbiencePlayerProvider>
  );
}

/**
 * Inner app - the BootProvider wraps it so any descendant (this component,
 * pages, hooks) can call `useBoot()` to drive the splash. The boot covers
 * the whole tree on first paint; we tell it to finish once auth resolves.
 */
function AppInner({ devMode }: { devMode: boolean }) {
  const { state } = useAuth();
  const location = useLocation();
  const boot = useBoot();
  const analyticsUserIdRef = useRef<string | null>(null);
  const analyticsScreenRef = useRef<string | null>(null);

  // Lands a visit that arrived through a Branch link on the surface that link
  // asked for. Waits for sign-in and navigates at most once; a no-op on every
  // ordinary visit.
  useBranchDeepLink();

  // Links this Teams tab's Microsoft identity to the member, whichever page
  // the tab opens on. A no-op outside the Teams wrapper.
  useTeamsTabLink();

  /*
   * Boot audio deliberately lives in usePostOnboardingBootFlow, not here. This
   * effect was a copy of that one with no handover gate, so every ordinary page
   * load snapped at the member, and the handover snapped twice. The ambience bed
   * is onboarding-only, so there is nothing here to fade either.
   */

  // Boot the interaction-sound layer once: wires the declarative data-cuelume-*
  // delegation and applies the persisted mute preference.
  useEffect(() => {
    initSound();
  }, []);

  // Follow the OS while the member's colour theme is "system". The stored
  // choice was already applied before first paint by index.html.
  useEffect(() => {
    initTheme();
  }, []);

  // A soft page cue when the top-level destination changes (Today, Snapshot,
  // Explore, Community, Settings). Moving between sub-tabs inside a section
  // (e.g. /challenges -> /challenges/leaderboards) is NOT a page change; those tab bars play
  // their own lighter tick, so gating on the first path segment keeps the two
  // sounds distinct and never double-fires. The ref skips the first render so a
  // cold load is silent.
  const lastSectionRef = useRef(topLevelSection(location.pathname));
  useEffect(() => {
    const section = topLevelSection(location.pathname);
    if (section === lastSectionRef.current) return;
    lastSectionRef.current = section;
    playSound("page");
  }, [location.pathname]);

  useEffect(() => {
    if (state.status === "signedIn") {
      // Re-runs on every token renew (AuthProvider hands us a new state object
      // each time), so this must not be `datadogRum.setUser`: that replaces the
      // whole usr object and would drop the company tag every few minutes.
      setDatadogUser(state.user.profile.sub);
      const userId = state.user.profile.sub;
      if (analyticsUserIdRef.current !== userId) {
        analytics.identify(userId);
        analytics.track(analyticsEvents.signedIn, {
          authentication_method: isHostAuthActive() ? "host" : "oidc",
        });
        analyticsUserIdRef.current = userId;
      }
      // Shared-browser hygiene: drop any other member's persisted Prybar
      // conversation (including legacy pre-userId "-guest" keys).
      purgeOtherUsersPrybarSessions(state.user.profile.sub);
      return;
    }
    if (state.status !== "signedOut" && state.status !== "hostError") return;
    // logged_out covers both a deliberate sign-out and an expired session -
    // either way it's a normal outcome, not a failure of the handshake
    // itself. Only timeout/refresh_failed represent the mechanism breaking.
    // Report before clearing the user so a mid-session failure (e.g. a 401
    // recovery timeout) still carries who was signed in when it happened -
    // this was otherwise invisible in RUM (a pure state transition, no
    // thrown error), so a "try again" screen with no corresponding event
    // would be undiagnosable after the fact.
    // An impersonation session ending (expiry or revoke) is an expected end,
    // not a mechanism failure, so it must not surface as a RUM error.
    if (state.status === "hostError" && state.reason !== "logged_out" && !isImpersonationActive()) {
      datadogRum.addError(new Error(`host-auth ${state.reason}`), { reason: state.reason });
    }
    clearDatadogUser();
    if (analyticsUserIdRef.current) {
      analytics.reset();
      analyticsUserIdRef.current = null;
      analyticsScreenRef.current = null;
    }
    clearPrybarStorage();
  }, [state]);

  useEffect(() => {
    if (state.status !== "signedIn") return;
    const screenName = analyticsScreenForPath(location.pathname);
    if (analyticsScreenRef.current === screenName) return;
    analytics.page(screenName);
    analyticsScreenRef.current = screenName;
  }, [location.pathname, state.status]);

  // Warm the other primary route chunks during idle so navigating to Snapshot,
  // Challenges, Explore, Community or Notifications hands straight to that
  // page's own skeleton instead of flashing the generic route fallback.
  // Idle-timed so it never competes with first paint or the landing chunk.
  useEffect(() => {
    if (state.status !== "signedIn") return;
    const warm = () => {
      void import("./pages/snapshot-page");
      void import("./pages/steps-page");
      void import("./pages/my-library-page");
      void import("./pages/community-page");
      void import("./pages/notifications-page");
    };
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(warm, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(id);
  }, [state.status]);

  // Slow-auth re-show. If a cached session takes longer than the splash
  // window we keep the boot up in `cold` mode so the user never lands on
  // a half-rendered shell. Gated by the cached onboarding flag AND
  // evidence of an OIDC session - a signed-out user with a stale
  // onboarding flag must not see a splash before the SignedOut page.
  useEffect(() => {
    // Host-auth handshake shows HostAuthLoading (G2), not the brand splash.
    if (isHostAuthActive()) return;
    if (state.status !== "loading") return;
    if (boot.visible) return;
    if (!localOnboardingFlag()) return;
    if (!hasOidcSession() && !localDevSignedIn()) return;
    // Only cover a handshake that is actually slow. Showing it the instant auth
    // began put the six-step `cold` animation in front of every load for a
    // member whose cached session resolves in a few hundred milliseconds, which
    // is the common case. Auth resolving cancels this before it ever paints.
    const timer = window.setTimeout(
      () => boot.show({ mode: "cold", status: "Connecting" }),
      SLOW_AUTH_SPLASH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [state.status, boot]);

  // Belt and braces: dismiss any stale splash if we're definitely not
  // heading to /today. A cleared session with a stale localStorage flag,
  // or a session that resolves into the onboarding wizard, both land
  // here and would otherwise see a 3.5s brand flash.
  //
  // Exception: post-onboarding handover. When the orchestrator calls
  // boot.show() before navigating away from /onboarding to Today, this
  // effect would otherwise immediately finish it (path is still
  // /onboarding during the await). The sessionStorage flag tells us
  // we're mid-handover and the splash should ride through to Today.
  useEffect(() => {
    if (!boot.visible) return;
    if (hasPostOnboardingHandoff()) return;
    const inNoSplashZone = NO_SPLASH_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    const signedOut = state.status === "signedOut";
    // A failed host-auth handshake renders HostAuthErrorScreen; without this the
    // splash stays up over it (returning users with a stale onboarding flag, or
    // the slow-auth cold splash) and hides the error.
    const hostError = state.status === "hostError";
    if (inNoSplashZone || signedOut || hostError) {
      boot.finish();
    }
  }, [boot, location.pathname, state.status]);

  /*
   * Public routes first, ahead of every auth-state branch below. See
   * PUBLIC_PATHS. No ScrollToTop and no DemoControlPanel: there is nothing to
   * navigate between here and no operator to summon a panel.
   */
  if (PUBLIC_PATHS.includes(location.pathname)) {
    return (
      <RootErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/install-landing" element={<InstallLandingPage />} />
            <Route path="/desktop/open" element={<DesktopAppPage />} />
          </Routes>
        </Suspense>
      </RootErrorBoundary>
    );
  }

  if (state.status === "unconfigured" && !devMode) {
    return <UnconfiguredScreen />;
  }

  // Host-auth mode (G2): hold a loader through the desktop handshake instead of
  // flashing the signed-out page or attempting a Keycloak redirect, and land a
  // failed/timed-out handshake on a coherent host-error surface. Gated on
  // isHostAuthActive() so the standalone web app never reaches this branch.
  if (isHostAuthActive()) {
    if (state.status === "loading") {
      return <HostAuthLoading />;
    }
    if (state.status === "hostError") {
      // An impersonation session that expired or was revoked lands here (the
      // impersonation token requester rejects on refresh). Show the
      // purpose-built ended screen with the right copy, not the desktop-shell
      // "reopen HealthOS" error, which is meaningless for an operator.
      return isImpersonationActive() ? (
        <ImpersonationEndedScreen />
      ) : (
        <HostAuthErrorScreen reason={state.reason} />
      );
    }
  }

  // Real OIDC takes precedence. In dev mode, the local signed-in shim is only
  // valid when auth is genuinely unconfigured. When OIDC is configured, an
  // expired or signed-out session must render SignedOutPage instead of a
  // signed-in shell that cannot attach a bearer token to protected media.
  const isSignedIn =
    state.status === "signedIn" ||
    (state.status === "unconfigured" && devMode && localDevSignedIn());

  return (
    <RootErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/silent-callback" element={<SilentCallbackPage />} />
          <Route path="/signup/:company/:groupCode?" element={<SignupLinkPage />} />
          {/* /desktop/open and /install-landing are handled by the
              PUBLIC_PATHS early return above, so they are deliberately not
              declared here. */}
          {isSignedIn ? (
            <Route path="/*" element={<SignedInRoutes />} />
          ) : (
            <Route path="/*" element={<SignedOutPage />} />
          )}
        </Routes>
      </Suspense>
      {/*
        Sprint 14: globally-mounted dev demo panel. Tree-shaken from
        production via the import.meta.env.DEV gate inside the
        component. Mounted here (not on AmbientPage) so the operator
        can summon it from any route - including /operations - via
        the bottom-right hot zone or Cmd+. shortcut.
      */}
      {isSignedIn && !location.pathname.startsWith("/onboarding") ? <DemoControlPanel /> : null}
      {/*
        The sign-in page's own demo affordance. The panel above cannot appear
        here: its permission read is keyed on a member, and there is not one
        yet. DEV-only, gated inside the component.
      */}
      {isSignedIn ? null : <PreLoginDemoButton />}
      <AwayRefreshModal isSignedIn={isSignedIn} />
    </RootErrorBoundary>
  );
}

/** Routes shown to a signed-in user inside the AppShell chrome. */
// Keeps the query string so OAuth round-trips landing on the old path (?connected=, ?error=) survive the rename.
function LegacyConnectionsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/connectors", search: location.search }} replace />;
}

function SignedInRoutes() {
  const location = useLocation();
  usePageAccent(location.pathname);
  useDarkModeGate();
  const boot = useBoot();
  const ambience = useAmbience();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const [productRechecking, setProductRechecking] = useState(false);
  const viewerFirstName = useMemberName()?.firstName ?? null;
  const [manualLogOpen, setManualLogOpen] = useState(false);
  const [manualLogPreselect, setManualLogPreselect] = useState<ManualLogPreselect | null>(null);
  // Memoized so the ManualLog context value is stable and does not churn the
  // attention controller's capture handler on every render.
  const openManualLog = useCallback((preselect?: ManualLogPreselect | null) => {
    setManualLogPreselect(preselect ?? null);
    setManualLogOpen(true);
  }, []);

  // Source of truth for onboarding state is the server
  // (onboarding_profiles.is_complete). localStorage was previously
  // used as a tie-breaker to handle the "freshly-completed onboarding"
  // race - the wizard's /complete POST flipped is_complete after the
  // SPA had already navigated, so a strict server-trust gate would
  // bounce the user back. The localStorage cache plugged that gap but
  // introduced a worse one: when the DB was wiped (dev seed reset, a
  // backend rollback, anything that erases onboarding_profiles), the
  // cached flag stayed "true" and the user was routed to /today
  // against an empty backend.
  //
  // The race is now handled by an optimistic setQueryData in the
  // wizard's finishOnboarding (sets the whoami cache to
  // onboardingComplete=true synchronously before navigating), so the
  // gate can trust the server exclusively. When the server says
  // false, we wipe the stale local flags so the wizard restarts
  // clean instead of inheriting half-finished state from a previous
  // session.
  const {
    data: whoamiData,
    isPending: whoamiPending,
    isError: whoamiError,
  } = useQuery({
    queryKey: queryKeys.whoamiOnboardingGate,
    queryFn: async () =>
      jsonOrThrow<WhoamiResponse>(await client().api.me.whoami.$get(), "GET /api/me/whoami"),
    staleTime: 60_000,
    // Rides out a token refresh: the first attempt can 401 while the new
    // token lands, and a routing decision made on that error sends an
    // onboarded user into the wizard.
    retry: 3,
  });

  // Retention. Stamps how many days since onboarding onto the RUM session and
  // counts one open per member per day, so "who came back on day 3" is a
  // group-by rather than a bespoke query. Anchored to the server's completion
  // timestamp, which is why it hangs off whoami rather than local state.
  useReturnTracking({
    userId: whoamiData?.userId ?? null,
    onboardingCompletedAt: whoamiData?.onboardingCompletedAt,
  });

  // Which customer this session belongs to, on every RUM event. Set here rather
  // than next to setUser because the id comes from the token and the company
  // comes from the server: the claim rides the access token (not the profile the
  // client reads), and host-auth sessions have no claims at all.
  //
  // Signing out calls clearUser(), which drops user properties, and the whoami
  // cache outlives it on some sign-out paths. Re-stamping is handled by this
  // component: it only renders while signed in, so a fresh sign-in remounts it
  // and runs the effect again rather than seeing an unchanged companyId and
  // leaving the whole session untagged.
  useEffect(() => {
    setDatadogUserCompany(whoamiData?.companyId);
  }, [whoamiData?.companyId]);

  useEffect(() => {
    if (!whoamiData) return;
    if (whoamiData.onboardingComplete) {
      setLocalOnboardingFlag(true);
      return;
    }
    /*
     * The wipe path below is meant for "user lands on / with stale
     * localStorage but the server has no profile" - the DB-wipe +
     * still-cached-browser case. It must NOT run while the user is
     * mid-wizard at /onboarding: the wizard has just set
     * thrive_post_onboarding_boot + called boot.show, and an effect
     * re-run triggered by `boot` dep change would read the
     * (still-false) cached whoami, wipe the handover flag, and
     * dismiss the splash before /complete had even fired.
     *
     * Path guard: while on /onboarding, the user is intentionally
     * onboarding; whoami=false is expected and should not trigger a
     * wipe.
     */
    if (location.pathname === "/onboarding" || location.pathname.startsWith("/onboarding/")) {
      return;
    }
    setLocalOnboardingFlag(false);
    try {
      localStorage.removeItem("thrive_onb_data");
      // The step pointer too: clearing the draft alone resumes the wizard on
      // the last screen it reached with no answers behind it.
      localStorage.removeItem("thrive_onb_step");
      sessionStorage.removeItem("thrive_post_onboarding_boot");
    } catch {
      /* storage unavailable; nothing to clean */
    }
    if (boot.visible) boot.finish();
  }, [whoamiData, boot, location.pathname]);

  // Server is the only authority. localStorage flags are cosmetic
  // (boot splash, returning-user UX) and never gate routing.
  const onboarded = whoamiData?.onboardingComplete ?? false;
  const hasCompanionAccess = whoamiData?.hasCompanionAccess === true;
  const companionMember = hasCompanionAccess && onboarded;
  const whoamiHasContactCentre = whoamiData?.hasContactCentreAccess === true;
  const isOnboardingRoute =
    location.pathname === "/onboarding" || location.pathname.startsWith("/onboarding/");
  const isOpsRoute = location.pathname.startsWith("/operations");
  const isContactCentreRoute =
    location.pathname === "/contact-centre" || location.pathname.startsWith("/contact-centre/");
  const isHomePath = location.pathname === "/";

  // The ambience bed belongs to onboarding alone. If a routing race ever
  // let the wizard mount long enough to start it (stale /onboarding, a
  // whoami blip mid token refresh), fade it out the moment an onboarded
  // user is anywhere else, so Today never has music playing from nowhere.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ambience methods are recreated per provider render; the route/gate flags are the triggers
  useEffect(() => {
    if (onboarded && !isOnboardingRoute) ambience.fadeOut(600);
  }, [onboarded, isOnboardingRoute]);

  const communityCapabilities = useCommunityCapabilities({
    enabled: !whoamiPending,
  });
  const holdsContactCentreRole = hasContactCentreRole(communityCapabilities.data?.roles ?? []);
  const contactCentreMe = useContactCentreMe({
    enabled: !whoamiPending && (whoamiHasContactCentre || holdsContactCentreRole),
  });
  const hasContactCentreMeAccess =
    contactCentreMe.data?.canAccessContactCentre === true &&
    contactCentreMe.data.capabilities.includes("cc:view");
  const contactCentreGatePending =
    !onboarded &&
    !whoamiHasContactCentre &&
    (communityCapabilities.isPending ||
      (holdsContactCentreRole && (contactCentreMe.isPending || contactCentreMe.isError)));
  const homeSurfaceInput = {
    onboarded,
    hasCompanion: hasCompanionAccess,
    hasContactCentre: whoamiHasContactCentre,
    hasOperations: communityCapabilities.data?.canAccessOperations === true,
    lastUsed: readLastUsedHomeSurface(whoamiData?.userId),
  };
  const recheckProductAccess = () => {
    if (productRechecking) return;
    setProductRechecking(true);
    void refresh()
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.whoamiOnboardingGate }),
          queryClient.invalidateQueries({ queryKey: COMMUNITY_CAPABILITIES_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: queryKeys.contactCentreMe }),
        ]),
      )
      .finally(() => setProductRechecking(false));
  };
  const productAccessDenied = (
    <ScreenAccessDenied
      variant="product"
      recheckStatus="idle"
      rechecking={productRechecking}
      onRecheck={recheckProductAccess}
    />
  );
  const productLookupFailed = (
    <ScreenAccessDenied
      variant="lookup"
      recheckStatus="idle"
      rechecking={productRechecking}
      onRecheck={recheckProductAccess}
    />
  );

  useEffect(() => {
    const userId = whoamiData?.userId;
    if (!userId || whoamiPending) return;
    if (isContactCentreRoute) {
      if (hasContactCentreMeAccess) writeLastUsedHomeSurface(userId, "contact-centre");
      return;
    }
    if (isOpsRoute || isOnboardingRoute || !onboarded || isHomePath || !hasCompanionAccess) return;
    writeLastUsedHomeSurface(userId, "member");
  }, [
    hasCompanionAccess,
    hasContactCentreMeAccess,
    isContactCentreRoute,
    isHomePath,
    isOnboardingRoute,
    isOpsRoute,
    onboarded,
    whoamiData?.userId,
    whoamiPending,
  ]);
  const { data: profileData } = useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () =>
      jsonOrThrow<NavProfileResponse>(await client().api.profile.$get(), "GET /api/profile"),
    enabled: companionMember && !isOnboardingRoute,
    staleTime: 60_000,
  });

  /*
   * Warm the Heartbeat recommended shelf once the member is through the gate.
   *
   * The shelf is the slowest read either surface makes (context build, a vector
   * search per content type, an LLM call for the "why"), and it is the first
   * thing on Explore. Fetched when Explore mounts, it lands after the catalog
   * and the member watches it appear late. Fetched here it is already in the
   * cache by the time they navigate, so the shelf paints with the page.
   *
   * Idle-timed, next to the route-chunk warm in App, so it never competes with
   * Today's own first paint. prefetchQuery is a no-op when the entry is already
   * fresh, so Today having asked for it first costs nothing here.
   *
   * The company challenge rides along. Challenges cannot ask for its dashboard
   * until it knows whether a challenge exists, so a cold visit is two reads in
   * series behind one skeleton; with this one already cached the page is one
   * read from content. Same staleTime as the page's own query.
   */
  useEffect(() => {
    if (!companionMember || isOnboardingRoute) return;
    const warm = () => {
      void queryClient.prefetchQuery(libraryRecommendationsQuery(client()));
      void queryClient.prefetchQuery({
        queryKey: queryKeys.companyWalkingChallenge,
        queryFn: () => getCompanyWalkingChallenge(client()),
        staleTime: 60_000,
      });
    };
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(warm, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(id);
  }, [companionMember, isOnboardingRoute, queryClient]);

  useEffect(() => {
    if (!companionMember) return;
    // In the desktop embed the native shell owns OS notifications (it polls the
    // inbox and raises them itself), so the web app must not also register for
    // browser Web Push or the user gets duplicate OS notifications.
    if (isHostAuthActive()) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    void syncWebPushSubscription();
  }, [companionMember]);

  // Refresh the notifications feed and Today attention state when the push
  // service worker reports an OS-notification click (sw.js posts
  // {kind: "attention.action"} to every open window).
  useServiceWorkerNotificationSync();

  // The interaction-sound preference is server-persisted on the onboarding
  // profile because the desktop embed's iframe localStorage does not survive a
  // relaunch. Adopt it once the profile loads; hydrateSoundPref no-ops when
  // this browser already holds an explicit local choice.
  const soundPref = profileData?.data?.sound;
  useEffect(() => {
    if (typeof soundPref === "boolean") hydrateSoundPref(soundPref);
  }, [soundPref]);

  // Notification chime: same durability story, independent toggle.
  const notificationSoundPref = profileData?.data?.notificationSound;
  useEffect(() => {
    if (typeof notificationSoundPref === "boolean") {
      hydrateNotificationSoundPref(notificationSoundPref);
    }
  }, [notificationSoundPref]);

  const prybarEnabled = readEnv().prybarEnabled;
  // Day Zero is a guided first run and Prybar is a way out of it: opening the
  // command bar mid-tour drops the member somewhere the tour did not send them
  // and the run stalls on a stop they never took. Unmounted rather than
  // disabled, so the wake word and the shortcut go with it. Unloaded counts as
  // running, so it cannot open in the gap before the state arrives.
  const dayZero = useDayZeroTour(prybarEnabled && companionMember);
  const dayZeroRunning = !dayZero.loaded || dayZero.stop !== null;
  // Every way into Prybar, not just the overlay: the header trigger and the
  // profile menu's feedback entry open the same surface, and leaving either one
  // live means the tour can still be walked out of.
  const prybarLive = prybarEnabled && companionMember && !dayZeroRunning;

  const navItems = useNavItemsWithBadge(companionMember && !isOnboardingRoute, {
    capabilities: communityCapabilities.data,
    hasContactCentreAccess: whoamiHasContactCentre || holdsContactCentreRole,
  });

  // Desktop embed has no boot splash to cover the window while the server
  // onboarding status loads, and the gate optimistically treats a pending
  // whoami as "not onboarded". Without this hold a returning user is briefly
  // routed through /onboarding, flashing the wizard and kicking off its
  // ambience loop, before whoami resolves and bounces them home. Holding on
  // the existing "Signing you in…" surface keeps the handoff seamless.
  if (isHostAuthActive() && whoamiPending) {
    return <HostAuthLoading />;
  }

  if (whoamiError && !whoamiData && !isContactCentreRoute && !isOpsRoute) {
    return productLookupFailed;
  }

  if (isOnboardingRoute) {
    if (companionMember) {
      return <Navigate to="/" replace />;
    }
    if (whoamiPending || contactCentreGatePending) return null;
    if (!hasCompanionAccess && !whoamiHasContactCentre && communityCapabilities.isPending) {
      return null;
    }
    const dest = resolveHomeSurface(homeSurfaceInput);
    if (dest === "contact-centre") {
      return <Navigate to="/contact-centre" replace />;
    }
    if (dest === "operations") {
      return <Navigate to="/operations" replace />;
    }
    if (dest === "no-access") return productAccessDenied;
    return <OnboardingPage />;
  }

  if (!onboarded && !isOpsRoute && !isContactCentreRoute) {
    if (whoamiPending || contactCentreGatePending) {
      return null;
    }
    const dest = resolveHomeSurface(homeSurfaceInput);
    if (dest === "contact-centre") {
      return <Navigate to="/contact-centre" replace />;
    }
    if (dest === "operations") {
      return <Navigate to="/operations" replace />;
    }
    if (dest === "no-access") return productAccessDenied;
    // The wizard resumes this once it finishes, so a sign-up link to a challenge still ends on that challenge.
    rememberOnboardingReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/onboarding" replace />;
  }

  if (onboarded && isHomePath) {
    if (communityCapabilities.isPending) return null;
    if (holdsContactCentreRole && (contactCentreMe.isPending || contactCentreMe.isError)) {
      return null;
    }
    const dest = resolveHomeSurface(homeSurfaceInput);
    if (dest === "contact-centre") {
      return <Navigate to="/contact-centre" replace />;
    }
    if (dest === "operations") {
      return <Navigate to="/operations" replace />;
    }
    if (dest === "no-access") return productAccessDenied;
    if (holdsContactCentreRole) {
      return <Navigate to="/today" replace />;
    }
  }

  if (isContactCentreRoute) {
    return (
      <MediaPlayerProvider>
        <ContactCentreShell companionExit={onboarded && hasCompanionAccess}>
          <Routes>
            <Route path="/contact-centre" element={<ContactCentreHome />} />
            <Route path="/contact-centre/*" element={<ContactCentreHome />} />
          </Routes>
        </ContactCentreShell>
      </MediaPlayerProvider>
    );
  }

  if (isOpsRoute) {
    return (
      <OpsShell companionExit={onboarded && hasCompanionAccess}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route
              path="/operations/coaching"
              element={
                <RequireOpsCap cap="ops:coach">
                  <CoachLayout />
                </RequireOpsCap>
              }
            >
              <Route index element={<Navigate to="coachees" replace />} />
              <Route path="coachees" element={<CoacheesPage />} />
              <Route path="coachees/:id" element={<CoacheePage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="availability" element={<AvailabilityPage />} />
            </Route>
            <Route
              path="/operations/coaching-ops"
              element={
                <RequireOpsCap cap="ops:coaching">
                  <CoachingOpsPage />
                </RequireOpsCap>
              }
            />
            <Route path="/operations/dashboard" element={<OperationsLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OperationsOverview />} />
              <Route path="cohort" element={<OperationsCohort />} />
              <Route path="calendar" element={<OperationsCalendar />} />
              <Route path="methodology" element={<OperationsMethodology />} />
            </Route>
            <Route
              path="/operations/people"
              element={
                <RequireOpsCap cap="ops:enrollment">
                  <OpsPeoplePage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/escalations"
              element={
                <RequireOpsCap cap="ops:member_data">
                  <OpsEscalationsPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/signals"
              element={
                <RequireOpsCap cap="ops:member_data">
                  <OpsSignalsPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex"
              element={
                <RequireOpsCap cap="ops:cortex">
                  <OpsCortexLoopHealthPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex/performance"
              element={
                <RequireOpsCap cap="ops:cortex">
                  <OpsCortexPerformancePage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex/queue"
              element={
                <RequireOpsCap cap="ops:cortex">
                  <OpsCortexScorerQueuePage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex/protocols"
              element={
                <RequireOpsCap cap="ops:cortex">
                  <OpsCortexProtocolsPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex/trace"
              element={
                <RequireOpsCap cap="ops:member_data">
                  <OpsCortexTracePage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/cortex/member"
              element={
                <RequireOpsCap cap="ops:member_data">
                  <OpsCortexMemberPage />
                </RequireOpsCap>
              }
            />
            <Route path="/operations/community" element={<CommunityAdminPage />} />
            <Route path="/operations/challenges" element={<ChallengesAdminPage />} />
            <Route
              path="/operations/broadcasts"
              element={
                <RequireOpsCap cap="ops:broadcast">
                  <OpsBroadcastsPage />
                </RequireOpsCap>
              }
            />
            <Route path="/operations/billing" element={<OpsBillingPage />} />
            <Route path="/operations/integrations" element={<OpsOrgIntegrationsPage />} />
            <Route
              path="/operations/admin/integrations"
              element={
                <RequireOpsCap cap="ops:integrations_admin">
                  <OpsIntegrationsAdminPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/admin/email"
              element={
                <RequireOpsCap cap="ops:integrations_admin">
                  <OpsEmailTemplatesPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/platform"
              element={
                <RequireOpsCap cap="ops:admin">
                  <OpsIntegrationsPage />
                </RequireOpsCap>
              }
            />
            <Route
              path="/operations/settings"
              element={
                <RequireOpsCap cap="ops:admin">
                  <OpsSettingsPage />
                </RequireOpsCap>
              }
            />
            <Route path="/operations/*" element={<Navigate to="/operations" replace />} />
          </Routes>
        </Suspense>
      </OpsShell>
    );
  }

  if (!hasCompanionAccess) {
    if (whoamiPending) return null;
    if (!whoamiHasContactCentre && communityCapabilities.isPending) return null;
    const dest = resolveHomeSurface(homeSurfaceInput);
    if (dest === "contact-centre") {
      return <Navigate to="/contact-centre" replace />;
    }
    if (dest === "operations") {
      return <Navigate to="/operations" replace />;
    }
    return productAccessDenied;
  }

  return (
    <MediaPlayerProvider>
      <PrybarScreenProvider>
        <ManualLogProvider value={openManualLog}>
          <AppShell
            brand={{ name: "Companion", eyebrow: "HealthOS" }}
            contentWidth="wide"
            navItems={navItems}
            sidebarFooter={<MediaPlayerSidebarDock />}
            pageUtilities={
              <div data-app-chrome="utilities" className="flex items-center justify-end gap-3">
                {prybarLive ? (
                  <div className="w-60 shrink-0">
                    <HealthOsCommandTrigger />
                  </div>
                ) : null}
                <ProfileNavTrigger
                  onFeedbackClick={
                    prybarLive ? () => openFeedbackSurvey(location.pathname) : undefined
                  }
                />
              </div>
            }
            onLogClick={() => openManualLog()}
          >
            {/* route chunks suspend under the shell, not the top-level null fallback */}
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<AmbientPage />} />
                <Route path="/today" element={<AmbientPage />} />
                <Route path="/snapshot" element={<SnapshotPage />} />
                <Route path="/logbook" element={<LogbookPage />} />
                <Route path="/challenges" element={<ChallengeEntry />} />
                {challengeRoutes()}
                <Route path="/anatomy" element={<AnatomyPage />} />
                <Route path="/fingerprint" element={<FingerprintPage />} />
                <Route
                  path="/fingerprint/heartbeat"
                  element={<FingerprintPage view="heartbeat" />}
                />
                <Route path="/fingerprint/whma" element={<FingerprintPage view="memory" />} />
                <Route
                  path="/heartbeat"
                  element={<LegacyRouteRedirect to="/fingerprint/heartbeat" />}
                />
                <Route path="/whma" element={<LegacyRouteRedirect to="/fingerprint/whma" />} />
                <Route path="/score" element={<Navigate to="/snapshot" replace />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route
                  path="/community/moments"
                  element={<LegacyCommunityRedirect filter="moments" />}
                />
                <Route
                  path="/community/kudos"
                  element={<LegacyCommunityRedirect filter="kudos" />}
                />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/admin/community"
                  element={<Navigate to="/operations/community" replace />}
                />
                <Route path="/explore" element={<ExploreEntry />} />
                <Route path="/explore/collection" element={<MyLibraryPage />} />
                {/* Preserve stored content links from both earlier route names. */}
                <Route
                  path="/collection"
                  element={<LegacyRouteRedirect to="/explore/collection" />}
                />
                <Route path="/library" element={<LegacyRouteRedirect to="/explore/collection" />} />
                <Route path="/connectors" element={<ConnectorsPage />} />
                <Route path="/connections" element={<LegacyConnectionsRedirect />} />
                <Route path="/link-chat" element={<LinkChatPage />} />
                <Route path="/coaching" element={<CoachingPage />} />
                <Route path="/care" element={<CoachingPage kind="care_team" />} />
                <Route path="/coaching/join" element={<CoachingJoinPage />} />
                <Route path="/coaching/join/:inviteId" element={<CoachingJoinPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/settings" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/design-system" element={<DesignSystemPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            {/* The desktop embed surfaces attention via native OS notifications and
            its own docked toast, so the in-app floating toast (and its chime) is
            suppressed there to avoid double-notifying. */}
            {!isHostAuthActive() && <InAppAttentionToast />}
            <BroadcastHost />
            <ImpersonationBanner />
            {prybarLive && <HealthOsCommandBar />}
            <ManualSignalLogModal
              open={manualLogOpen}
              preselect={manualLogPreselect}
              onOpenChange={(nextOpen) => {
                setManualLogOpen(nextOpen);
                if (!nextOpen) setManualLogPreselect(null);
              }}
            />
            <RouteMilestoneAnnouncer viewerFirstName={viewerFirstName} />
          </AppShell>
        </ManualLogProvider>
      </PrybarScreenProvider>
    </MediaPlayerProvider>
  );
}

const PULSE_DURATION_MS = 1500;

function useNavItemsWithBadge(
  enabled: boolean,
  access?: {
    capabilities?: api.CommunityCapabilitiesResponse;
    hasContactCentreAccess?: boolean;
  },
): DefaultNavItem[] {
  const communityCapabilities = access?.capabilities;
  const showContactCentreNav =
    access?.hasContactCentreAccess === true ||
    hasContactCentreRole(communityCapabilities?.roles ?? []);
  const { data } = useAttentionEvents({ enabled });
  const count = data?.events.length ?? 0;
  const { count: unreadCount } = useUnreadNotificationCount({ enabled });
  const { count: unreadKudosCount } = useUnreadKudos({ enabled });
  const coaching = useQuery({
    queryKey: queryKeys.coachingProgrammes,
    queryFn: () => getCoachingProgrammes(client()),
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const opsMe = useOperationsMe({ enabled: communityCapabilities?.canAccessOperations === true });
  const previousCount = useRef(count);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    if (count > previousCount.current) {
      setPulsing(true);
      const t = window.setTimeout(() => setPulsing(false), PULSE_DURATION_MS);
      previousCount.current = count;
      return () => window.clearTimeout(t);
    }
    previousCount.current = count;
  }, [count]);

  const decorate = (item: DefaultNavItem): DefaultNavItem => {
    if (item.id === "ambient") {
      const next = { ...item };
      if (count > 0) next.badge = count;
      if (pulsing) next.pulse = true;
      if (showContactCentreNav) next.path = "/today";
      return next;
    }
    if (item.id === "notifications") {
      const next = { ...item };
      if (unreadCount > 0) next.badge = unreadCount;
      return next;
    }
    if (item.id === "community") {
      // Unread received kudos; same source the You tab badge reads, so the
      // two decrement together on mark-as-read (one notifications
      // invalidation drives both).
      const next = { ...item };
      if (unreadKudosCount > 0) next.badge = unreadKudosCount;
      return next;
    }
    return item.children ? { ...item, children: item.children.map(decorate) } : item;
  };
  const baseItems: DefaultNavItem[] = DEFAULT_NAV_ITEMS.map(decorate);
  const extras: DefaultNavItem[] = [];
  if (showContactCentreNav) {
    extras.push({
      id: "contact-centre",
      label: "Contact Centre",
      compactLabel: "Centre",
      path: "/contact-centre",
      Icon: Headset,
      hideOnMobile: true,
      group: "utility",
    });
  }
  if (communityCapabilities?.canAccessOperations) {
    extras.push({
      id: "operations",
      label: opsBrandFor(opsMe.data ?? null),
      compactLabel: "Ops",
      path: "/operations",
      Icon: ShieldCheck,
      hideOnMobile: true,
      group: "utility",
    });
  }
  // A coach or care team is personal and ongoing, so it sits with Today and
  // Snapshot rather than among the places to explore.
  const afterSnapshot = baseItems.findIndex((item) => item.id === "snapshot") + 1;
  for (const [i, p] of (coaching.data?.programmes ?? []).entries()) {
    const at = afterSnapshot + i;
    const waiting = p.unreadMessages + p.waiting;
    const words = careWords(p.kind);
    baseItems.splice(at, 0, {
      id: p.kind === "care_team" ? "care" : "coaching",
      label: words.area,
      path: words.path,
      Icon: p.kind === "care_team" ? FirstAidKit : ChatsCircle,
      ...(waiting > 0 ? { badge: waiting } : {}),
    });
  }
  return extras.length > 0 ? [...baseItems, ...extras] : baseItems;
}

function HostAuthLoading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream px-6">
      <div className="w-24 animate-pulse text-ink-primary">
        <ThriveLeaf />
      </div>
      <p className="text-sm text-ink-body leading-relaxed">Signing you in…</p>
    </div>
  );
}

const HOST_AUTH_ERROR_COPY: Record<HostAuthErrorReason, string> = {
  timeout: "We couldn't reach the desktop app to sign you in. Try reopening HealthOS.",
  logged_out: "You've been signed out of the desktop app. Sign in there to continue.",
  refresh_failed: "We couldn't refresh your desktop session. Try reopening HealthOS.",
};

function HostAuthErrorScreen({ reason }: { reason: HostAuthErrorReason }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <div className="max-w-md rounded-xl bg-surface-elevated p-8 text-center shadow-thrive">
        <div className="mx-auto mb-5 w-16 text-ink-primary">
          <ThriveLeaf />
        </div>
        <h1 className="font-title text-2xl text-ink-primary">Can't sign you in</h1>
        <p className="mt-2 text-sm text-ink-body leading-relaxed">{HOST_AUTH_ERROR_COPY[reason]}</p>
        <Button variant="primary" className="mt-6" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    </div>
  );
}

/**
 * Terminal screen for an impersonation session that has expired (15-minute
 * token) or been revoked. Deliberately offers no "Try again": the
 * one-time code is spent, so a reload cannot recover the session. The operator
 * returns to the admin console to start a new one.
 */
function ImpersonationEndedScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <div className="max-w-md rounded-xl bg-surface-elevated p-8 text-center shadow-thrive">
        <div className="mx-auto mb-5 w-16 text-ink-primary">
          <ThriveLeaf />
        </div>
        <h1 className="font-title text-2xl text-ink-primary">Impersonation session ended</h1>
        <p className="mt-2 text-sm text-ink-body leading-relaxed">
          This read-only session has expired or been revoked. To keep reviewing this member, return
          to the admin console and start a new "Log in as user" session.
        </p>
        <Button variant="primary" className="mt-6" onClick={() => window.close()}>
          Close this tab
        </Button>
      </div>
    </div>
  );
}

function UnconfiguredScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <div className="max-w-lg rounded-xl bg-surface-elevated p-8 shadow-thrive">
        <h1 className="font-title text-2xl text-ink-primary">HealthOS isn't configured</h1>
        <p className="mt-2 text-sm text-ink-body leading-relaxed">
          Set{" "}
          <code className="rounded bg-surface-track px-1.5 py-0.5 text-xs">
            VITE_OIDC_AUTHORITY
          </code>{" "}
          and{" "}
          <code className="rounded bg-surface-track px-1.5 py-0.5 text-xs">
            VITE_OIDC_CLIENT_ID
          </code>{" "}
          in Cloudflare Pages variables or your local{" "}
          <code className="rounded bg-surface-track px-1.5 py-0.5 text-xs">.env</code> file.
        </p>
      </div>
    </div>
  );
}
